import { Checkbox, DefaultButton, IconButton, IIconProps, Modal, Text, TextField } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import React, { useEffect, useState } from 'react';
import { keyPress } from 'app/components/helpers';
import './BugInformationModal.scss';
import { BugInformation, BugInformations, RequestDto, RequestType } from 'generated/request-backend';

const titleId = 'bug-info-modal';

const cancelIcon: IIconProps = { iconName: 'Cancel' };

interface IProps {
    modalState: boolean;
    updateModalState: Function;
    selectedRequest: RequestDto;
    onUpdateHandler?: (bugInformations: BugInformations[]) => void;
}

const BugInformationModal: React.FC<IProps> = ({ modalState, updateModalState, selectedRequest, onUpdateHandler }) => {
    const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(modalState);
    const [bugInformations, setBugInformations] = useState<IBugUpdates>({});
    const [selectedBugs, setSelectedBugs] = useState<ISelectedFields>({});
    const [errorFields, setErrorFields] = useState<ISelectedFields>({});

    const handleModalDismiss = () => {
        hideModal();
        updateModalState(false);
        setBugInformations({});
        setSelectedBugs({});
    };

    interface IBugUpdates {
        pleBug?: string;
        msftBug?: string;
        osBug?: string;
        hubOsBug?: string;
        otherBug?: string;
        azureDevopsLink?: string;
    }

    interface ISelectedFields {
        [key: string]: boolean;
    }

    const handleChangeTextField = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, type: number) => {
        const value = (e.target as HTMLInputElement | HTMLTextAreaElement).value;
        setErrorFields((val: ISelectedFields) => ({ ...val, [type]: false }));
        switch (type) {
            case 1:
                setBugInformations((val: IBugUpdates) => ({ ...val, pleBug: value }));
                break;
            case 2:
                setBugInformations((val: IBugUpdates) => ({ ...val, msftBug: value }));
                break;
            case 3:
                setBugInformations((val: IBugUpdates) => ({ ...val, osBug: value }));
                break;
            case 4:
                setBugInformations((val: IBugUpdates) => ({ ...val, hubOsBug: value }));
                break;
            case 5:
                setBugInformations((val: IBugUpdates) => ({ ...val, otherBug: value }));
                break;
            case 6:
                setBugInformations((val: IBugUpdates) => ({ ...val, azureDevopsLink: value }));
                break;
            default:
                break;
        }
    };

    const handleChangeCheckbox = (e: React.FormEvent<HTMLInputElement | HTMLElement>, isChecked: boolean, type: number) => {
        setSelectedBugs((val: ISelectedFields) => ({ ...val, [type]: isChecked }));
    };

    const handleSave = () => {
        let bugInformationsData : BugInformations[] = [];
        let errorFlag = false;
        let errorData = { ...errorFields };
        for (const key in selectedBugs) {
            if (selectedBugs[key] === true) {
                let bugUrl = '';
                switch (parseInt(key)) {
                    case 1:
                        bugUrl = bugInformations.pleBug;
                        break;
                    case 2:
                        bugUrl = bugInformations.msftBug;
                        break;
                    case 3:
                        bugUrl = bugInformations.osBug;
                        break;
                    case 4:
                        bugUrl = bugInformations.hubOsBug;
                        break;
                    case 5:
                        bugUrl = bugInformations.otherBug;
                        break;
                    case 6:
                        bugUrl = bugInformations.azureDevopsLink;
                        break;
                    default:
                        break;
                }
                if (!bugUrl || bugUrl === '') {
                    errorData = { ...errorData, [key]: true };
                    errorFlag = true;
                } else {
                    errorData = { ...errorData, [key]: false };
                }
                bugInformationsData.push(new BugInformations({
                    bugInformation: parseInt(key),
                    url: bugUrl
                }));
            } else {
                errorData = { ...errorData, [key]: false };
            }
        }
        setErrorFields(errorData);
        if (errorFlag) {
            return false;
        }
        if (
            (bugInformationsData && bugInformationsData.length > 0) ||
            (bugInformationsData &&
                bugInformationsData.length === 0 &&
                selectedRequest.bugInformations &&
                selectedRequest.bugInformations.length > 0)
        ) {
            onUpdateHandler(bugInformationsData);
            setBugInformations({});
            setSelectedBugs({});
            updateModalState(false);
        }
    };

    useEffect(() => {
        if (modalState) {
            showModal();
        } else {
            hideModal();
            setBugInformations({});
            setSelectedBugs({});
        }
    }, [modalState, showModal, hideModal]);

    useEffect(() => {
        setBugInformations({});
        setSelectedBugs({});
        if (selectedRequest) {
            if (selectedRequest.bugInformations && selectedRequest.bugInformations.length > 0) {
                for (const bug of selectedRequest.bugInformations) {
                    setSelectedBugs((val: ISelectedFields) => ({ ...val, [bug.bugInformation]: true }));
                    switch (bug.bugInformation) {
                        case BugInformation.PLE_Bug:
                            setBugInformations((val: IBugUpdates) => ({ ...val, pleBug: bug.url }));
                            break;
                        case BugInformation.MSFT_Devices_Non_PLE_Bug:
                            setBugInformations((val: IBugUpdates) => ({ ...val, msftBug: bug.url }));
                            break;
                        case BugInformation.OS_Bug:
                            setBugInformations((val: IBugUpdates) => ({ ...val, osBug: bug.url }));
                            break;
                        case BugInformation.Hub_OS_Bug:
                            setBugInformations((val: IBugUpdates) => ({ ...val, hubOsBug: bug.url }));
                            break;
                        case BugInformation.Other_Bugs:
                            setBugInformations((val: IBugUpdates) => ({ ...val, otherBug: bug.url }));
                            break;
                        case BugInformation.Azure_Devops_Link:
                            setBugInformations((val: IBugUpdates) => ({ ...val, azureDevopsLink: bug.url }));
                            break;
                        default:
                            break;
                    }
                }
            }
        }
    }, [selectedRequest, modalState]);

    return (
        <Modal
            titleAriaId={titleId}
            isOpen={isModalOpen}
            onDismiss={handleModalDismiss}
            isBlocking={false}
            containerClassName="bug-info-modal">
            <>
                <div className="modal-head">
                    <Text>
                        {selectedRequest.requestType === RequestType.CssReviewRequest ||
                        selectedRequest.requestType === RequestType.CXP_TechnicalRequest
                            ? 'Add bugs'
                            : 'Add Azure DevOps link'}
                    </Text>
                    <IconButton
                        role="button"
                        className="modal-close"
                        iconProps={cancelIcon}
                        ariaLabel="Close popup modal"
                        onClick={handleModalDismiss}
                        onKeyPress={(e) => keyPress(e, handleModalDismiss)}
                        tabIndex={0}
                    />
                </div>
                <div className="bug-info-modal-body">
                    <>
                        <div className="scroll-panel">
                            <div className="dp-content">
                                {selectedRequest.requestType === RequestType.CssReviewRequest ||
                                selectedRequest.requestType === RequestType.CXP_TechnicalRequest ? (
                                    <>
                                        {/*if request type is CXP - Technical or CSS - Review Request display 5 bug links */}
                                        <div className="bug-info-control-group">
                                            <div className="checkbox-wrap">
                                                <Checkbox
                                                    data-testid="ple-bug-checkbox"
                                                    label="PLE Bug"
                                                    onChange={(e, ischecked) => handleChangeCheckbox(e, ischecked, 1)}
                                                    checked={selectedBugs[1] ? true : false}
                                                />
                                            </div>
                                            <div className="textfield-wrap">
                                                <TextField
                                                    data-testid="ple-bug-test-field"
                                                    value={bugInformations?.pleBug}
                                                    borderless
                                                    onChange={(e) => handleChangeTextField(e, 1)}
                                                    disabled={!selectedBugs[1]}
                                                />
                                            </div>
                                            {errorFields[1] === true && (
                                                <div className="error-msg">Please enter a valid bug information</div>
                                            )}
                                        </div>
                                        <div className="bug-info-control-group">
                                            <div className="checkbox-wrap">
                                                <Checkbox
                                                    label="MSFT Devices Non PLE Bug"
                                                    onChange={(e, ischecked) => handleChangeCheckbox(e, ischecked, 2)}
                                                    checked={selectedBugs[2] ? true : false}
                                                />
                                            </div>
                                            <div className="textfield-wrap">
                                                <TextField
                                                    data-testid="MSFT-nonPLE-bug-text-field"
                                                    value={bugInformations?.msftBug}
                                                    borderless
                                                    onChange={(e) => handleChangeTextField(e, 2)}
                                                    disabled={!selectedBugs[2]}
                                                />
                                            </div>
                                            {errorFields[2] === true && (
                                                <div className="error-msg">Please enter a valid bug information</div>
                                            )}
                                        </div>
                                        <div className="bug-info-control-group">
                                            <div className="checkbox-wrap">
                                                <Checkbox
                                                    label="OS Bug"
                                                    onChange={(e, ischecked) => handleChangeCheckbox(e, ischecked, 3)}
                                                    checked={selectedBugs[3] ? true : false}
                                                />
                                            </div>
                                            <div className="textfield-wrap">
                                                <TextField
                                                    data-testid="OS-bug-text-field"
                                                    value={bugInformations?.osBug}
                                                    borderless
                                                    onChange={(e) => handleChangeTextField(e, 3)}
                                                    disabled={!selectedBugs[3]}
                                                />
                                            </div>
                                            {errorFields[3] === true && (
                                                <div className="error-msg">Please enter a valid bug information</div>
                                            )}
                                        </div>
                                        <div className="bug-info-control-group">
                                            <div className="checkbox-wrap">
                                                <Checkbox
                                                    label="Hub OS Bug"
                                                    onChange={(e, ischecked) => handleChangeCheckbox(e, ischecked, 4)}
                                                    checked={selectedBugs[4] ? true : false}
                                                />
                                            </div>
                                            <div className="textfield-wrap">
                                                <TextField
                                                    data-testid="Hub-OS-Bug-text-field"
                                                    value={bugInformations?.hubOsBug}
                                                    borderless
                                                    onChange={(e) => handleChangeTextField(e, 4)}
                                                    disabled={!selectedBugs[4]}
                                                />
                                            </div>
                                            {errorFields[4] === true && (
                                                <div className="error-msg">Please enter a valid bug information</div>
                                            )}
                                        </div>
                                        <div className="bug-info-control-group">
                                            <div className="checkbox-wrap">
                                                <Checkbox
                                                    label="Other Bugs"
                                                    onChange={(e, ischecked) => handleChangeCheckbox(e, ischecked, 5)}
                                                    checked={selectedBugs[5] ? true : false}
                                                />
                                            </div>
                                            <div className="textfield-wrap">
                                                <TextField
                                                    data-testid="other-bugs-text-field"
                                                    value={bugInformations?.otherBug}
                                                    borderless
                                                    onChange={(e) => handleChangeTextField(e, 5)}
                                                    disabled={!selectedBugs[5]}
                                                />
                                            </div>
                                            {errorFields[5] === true && (
                                                <div className="error-msg">Please enter a valid bug information</div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/*if request type is CXP - PFR, display the ADO link */}
                                        <div className="bug-info-control-group">
                                            <div className="checkbox-wrap">
                                                <Checkbox
                                                    label="Azure Devops Link"
                                                    onChange={(e, ischecked) => handleChangeCheckbox(e, ischecked, 6)}
                                                    checked={selectedBugs[6] ? true : false}
                                                />
                                            </div>
                                            <div className="textfield-wrap">
                                                <TextField
                                                    data-testid="azure-devops-link-text-field"
                                                    value={bugInformations?.azureDevopsLink}
                                                    borderless
                                                    onChange={(e) => handleChangeTextField(e, 6)}
                                                    disabled={!selectedBugs[6]}
                                                />
                                            </div>
                                            {errorFields[6] === true && (
                                                <div className="error-msg">Please enter a valid Azure DevOps Link</div>
                                            )}
                                        </div>
                                    </>
                                )}
                                <div className="form-btn-wrap">
                                    <DefaultButton className="default" onClick={handleModalDismiss} data-testid="cancel-button">
                                        Cancel
                                    </DefaultButton>
                                    <DefaultButton
                                        data-testid="update-btn-test"
                                        type="submit"
                                        className="primary m-l-20"
                                        onClick={handleSave}>
                                        Update
                                    </DefaultButton>
                                </div>
                            </div>
                        </div>
                    </>
                </div>
            </>
        </Modal>
    );
};

export default BugInformationModal;
